import React, { useState, useEffect, useRef } from 'react';

const ETACountdown = ({ initialMinutes = 18, liveMinutes, onUpdate }) => {
  const [minutes, setMinutes] = useState(initialMinutes);
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef(null);

  // Reset countdown whenever a live ETA update arrives from Supabase
  useEffect(() => {
    if (liveMinutes != null && liveMinutes !== minutes) {
      setMinutes(liveMinutes);
      setSeconds(0);
      onUpdate?.(liveMinutes);
    }
  }, [liveMinutes]);

  // Tick-down every second
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setSeconds(prev => {
        if (prev === 0) {
          setMinutes(m => {
            if (m <= 0) {
              clearInterval(timerRef?.current);
              return 0;
            }
            const next = m - 1;
            onUpdate?.(next);
            return next;
          });
          return 59;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef?.current);
  }, []);

  const totalInitialSeconds = (liveMinutes ?? initialMinutes) * 60;
  const remainingSeconds = minutes * 60 + seconds;
  const progress = totalInitialSeconds > 0
    ? ((totalInitialSeconds - remainingSeconds) / totalInitialSeconds) * 100
    : 0;

  const urgencyColor = minutes < 3 ? '#ef4444' : minutes < 8 ? '#f59e0b' : '#ffd700';

  return (
    <div className="bg-[#0a1628] rounded-2xl px-4 py-3 flex items-center gap-4 shadow-lg border border-[#ffd700]/20">
      {/* Circular progress */}
      <div className="relative w-14 h-14 flex-shrink-0">
        <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
          <circle cx="28" cy="28" r="22" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
          <circle
            cx="28" cy="28" r="22" fill="none"
            stroke={urgencyColor}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 22}`}
            strokeDashoffset={`${2 * Math.PI * 22 * (1 - progress / 100)}`}
            style={{ transition: 'stroke-dashoffset 1s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill={urgencyColor} className="w-5 h-5">
            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" />
          </svg>
        </div>
      </div>
      {/* ETA info */}
      <div className="flex-1 min-w-0">
        <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-0.5">Estimated Arrival</p>
        <div className="flex items-baseline gap-1.5">
          <span className="text-3xl font-black tabular-nums" style={{ color: urgencyColor }}>
            {String(minutes)?.padStart(2, '0')}
          </span>
          <span className="text-lg font-bold text-white/40">:</span>
          <span className="text-3xl font-black tabular-nums" style={{ color: urgencyColor }}>
            {String(seconds)?.padStart(2, '0')}
          </span>
          <span className="text-sm text-white/40 font-medium ml-1">min</span>
        </div>
      </div>
      {/* Distance / live indicator */}
      <div className="text-right flex-shrink-0">
        <p className="text-white/40 text-xs mb-0.5">Distance</p>
        <p className="text-white font-bold text-sm">2.4 km</p>
        <div className="flex items-center justify-end gap-1 mt-0.5">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
          <p className="text-green-400/80 text-xs">live</p>
        </div>
      </div>
    </div>
  );
};

export default ETACountdown;
